// Procedural texture generators for export-quality material enhancements
import * as THREE from 'three'

/** Noise-based roughness map — adds micro-surface variation for photorealism */
export const createRoughnessNoiseMap = (resolution: number, intensity: number): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = resolution
  canvas.height = resolution
  const ctx = canvas.getContext('2d')!
  const imageData = ctx.createImageData(resolution, resolution)
  const px = imageData.data

  // Use base gray (128) with noise perturbation
  const base = 128
  const range = Math.round(intensity * 128)

  for (let i = 0; i < px.length; i += 4) {
    const noise = base + Math.round((Math.random() - 0.5) * 2 * range)
    const clamped = Math.min(255, Math.max(0, noise))
    px[i] = clamped
    px[i + 1] = clamped
    px[i + 2] = clamped
    px[i + 3] = 255
  }

  // Apply simple 3×3 box blur to smooth the noise
  const blurred = ctx.createImageData(resolution, resolution)
  const bp = blurred.data
  const w = resolution
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      let sum = 0
      let count = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const sx = Math.min(w - 1, Math.max(0, x + dx))
          const sy = Math.min(w - 1, Math.max(0, y + dy))
          sum += px[(sy * w + sx) * 4]
          count++
        }
      }
      const avg = Math.round(sum / count)
      const idx = (y * w + x) * 4
      bp[idx] = avg
      bp[idx + 1] = avg
      bp[idx + 2] = avg
      bp[idx + 3] = 255
    }
  }

  ctx.putImageData(blurred, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.LinearSRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  return tex
}

/** Brushed metal normal map — anisotropic horizontal lines for realistic metal surface */
export const createBrushedMetalNormalMap = (resolution: number, strength: number): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = resolution
  canvas.height = resolution
  const ctx = canvas.getContext('2d')!
  const imageData = ctx.createImageData(resolution, resolution)
  const px = imageData.data

  // Normal map: (128, 128, 255) = flat surface, deviations in R/G encode surface tilt
  // Brushed metal: only vary the G channel (vertical perturbation = horizontal lines)
  const perturbRange = Math.round(strength * 40)

  for (let y = 0; y < resolution; y++) {
    // Each scanline gets a base perturbation (coherent horizontal line)
    const linePerturb = Math.round((Math.random() - 0.5) * 2 * perturbRange)
    for (let x = 0; x < resolution; x++) {
      // Add subtle per-pixel noise on top of the line
      const microNoise = Math.round((Math.random() - 0.5) * perturbRange * 0.3)
      const idx = (y * resolution + x) * 4
      px[idx] = 128 + microNoise // R — minimal X perturbation
      px[idx + 1] = Math.min(255, Math.max(0, 128 + linePerturb + microNoise)) // G — Y perturbation (horizontal lines)
      px[idx + 2] = 255 // B — Z always pointing up
      px[idx + 3] = 255
    }
  }

  ctx.putImageData(imageData, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.LinearSRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  return tex
}

/** Noise-based normal map — random surface perturbation for generic texture */
export const createNoiseNormalMap = (resolution: number, strength: number): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = resolution
  canvas.height = resolution
  const ctx = canvas.getContext('2d')!
  const imageData = ctx.createImageData(resolution, resolution)
  const px = imageData.data

  const perturbRange = Math.round(strength * 50)

  for (let i = 0; i < px.length; i += 4) {
    const nx = Math.round((Math.random() - 0.5) * 2 * perturbRange)
    const ny = Math.round((Math.random() - 0.5) * 2 * perturbRange)
    px[i] = Math.min(255, Math.max(0, 128 + nx))
    px[i + 1] = Math.min(255, Math.max(0, 128 + ny))
    px[i + 2] = 255
    px[i + 3] = 255
  }

  // Box blur to smooth the noise
  const blurred = ctx.createImageData(resolution, resolution)
  const bp = blurred.data
  const w = resolution
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      let sumR = 0
      let sumG = 0
      let count = 0
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const sx = Math.min(w - 1, Math.max(0, x + dx))
          const sy = Math.min(w - 1, Math.max(0, y + dy))
          const si = (sy * w + sx) * 4
          sumR += px[si]
          sumG += px[si + 1]
          count++
        }
      }
      const idx = (y * w + x) * 4
      bp[idx] = Math.round(sumR / count)
      bp[idx + 1] = Math.round(sumG / count)
      bp[idx + 2] = 255
      bp[idx + 3] = 255
    }
  }

  ctx.putImageData(blurred, 0, 0)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.LinearSRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  return tex
}
